import { useEffect, useId, useState } from "react";
import {
  AlertCircle,
  Bell,
  Building2,
  Calendar,
  Flag,
  Inbox,
  Loader2,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/cn";
import { useDrawerA11y } from "@/hooks/useDrawerA11y";
import { useClientTab } from "@/hooks/useClients";
import { useCreateTask } from "@/hooks/useTasks";
import { useCreateClient360Task } from "@/hooks/useClient360Tasks";
import { ResponsibleSelect } from "@/components/client360/ResponsibleSelect";
import { WatcherPicker } from "./WatcherPicker";
import { ClientPickerDrawer } from "@/components/home/ClientPickerDrawer";
import { AttachmentUpload, type PendingAttachment } from "@/components/attachments/AttachmentUpload";
import { PRIORITY_LABELS, quickDatePlus, REMINDER_OPTIONS } from "./constants";
import type { Client360TaskCreatePayload, TaskSubtype } from "@/api/client360Tasks";
import type {
  TaskCreatePayload,
  TaskDetailResponse,
  TaskPriorityApi,
  TaskReminderOffset,
  TaskWatcher,
} from "@/api/types";

interface ContactLite { id: string; name: string; position?: string }

/** Manual subtypes (R2/AC-T24) — bi_signal is SYSTEM-only, shown disabled. */
const MANUAL_SUBTYPES: Array<{ k: Exclude<TaskSubtype, "bi_signal">; label: string }> = [
  { k: "generic", label: "Завдання" },
  { k: "call", label: "Дзвінок" },
  { k: "follow_up", label: "Follow-up" },
];

export interface TaskCreateEntry {
  /** Client360/holding context — client locked, contacts available. */
  holdingId?: string;
  holdingName?: string;
  /** READ-ONLY provenance label shown in «Джерело» (prefill from entry-point). */
  source?: string;
  defaultSubtype?: Exclude<TaskSubtype, "bi_signal">;
}

/**
 * D-055 S3 — staged-mode (P7): дровер ПОВЕРТАЄ spec замість POST.
 * Реальна Task створюється server-side на submit звіту (одна транзакція,
 * Action Engine). ОДНА форма на CRM — staged = prop, не нова форма.
 */
export interface StagedTaskSpec {
  kind: "task" | "follow_up";
  title: string;
  dueDate: string;
  description?: string;
}

/**
 * Task Mgmt v1.2 — CANONICAL TaskCreateDrawer (D-031 / AC-T25): the ONE create
 * form, reused from /tasks, Home «+Створити», Client360 (entry.holdingId) and
 * Contacts. Merges the F3 CreateTaskDrawer + 3A Client360TaskDrawer families.
 *  · subtype = 3 manual (Завдання/Дзвінок/Follow-up); BI-сигнал disabled (system)
 *  · client = HOLDING-grain picker (ClientPickerDrawer) + явний режим «Загальна
 *    задача» (client optional — Igor decision; VISUAL «*» = required для клієнтської)
 *  · dueDate required · assignee = ResponsibleSelect (Q3: manager locked-self)
 *  · «Джерело» READ-ONLY prefill from the entry-point (no fake provenance)
 * Routing: client task → POST /client360/tasks (subtype/assignee/contact);
 * general → POST /tasks (+subtype, assignee Q3-asserted server-side).
 */
export function TaskCreateDrawer({
  open,
  onClose,
  entry,
  onCreated,
  staged,
}: {
  open: boolean;
  onClose: () => void;
  entry?: TaskCreateEntry | null;
  onCreated?: (created: TaskDetailResponse) => void;
  /** D-055 S3: staged-mode — onStage(spec) замість POST (звіт зустрічі). */
  staged?: { onStage: (spec: StagedTaskSpec) => void } | null;
}) {
  const panelRef = useDrawerA11y(open, onClose);
  const headingId = useId();

  const clientLocked = !!entry?.holdingId;

  const [subtype, setSubtype] = useState<Exclude<TaskSubtype, "bi_signal">>(entry?.defaultSubtype ?? "generic");
  const [title, setTitle] = useState("");
  const [mode, setMode] = useState<"client" | "general">(clientLocked ? "client" : "general");
  const [holding, setHolding] = useState<{ id: string; name: string } | null>(
    entry?.holdingId ? { id: entry.holdingId, name: entry.holdingName ?? "" } : null,
  );
  const [pickerOpen, setPickerOpen] = useState(false);
  const [contactId, setContactId] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [priority, setPriority] = useState<TaskPriorityApi>("medium");
  const [reminder, setReminder] = useState<TaskReminderOffset>("none");
  const [assigneeId, setAssigneeId] = useState("");
  // D-142.W1 — спостерігачі create-time (атомарно з задачею, П-6).
  const [watchers, setWatchers] = useState<TaskWatcher[]>([]);
  const [description, setDescription] = useState("");
  // TASK-ATT v1.3 (W1) — create-flow attachments (no parent yet → parentId='').
  const [pendingAtt, setPendingAtt] = useState<PendingAttachment[]>([]);
  const [attachmentsIds, setAttachmentsIds] = useState<string[]>([]);
  const [errors, setErrors] = useState<{ title?: string; client?: string; dueDate?: string; submit?: string }>({});

  const effectiveHoldingId = mode === "client" ? holding?.id ?? "" : "";
  const contactsQ = useClientTab<ContactLite>(effectiveHoldingId, "contacts", open && !!effectiveHoldingId);

  const mutationF3 = useCreateTask();
  const mutation3A = useCreateClient360Task(effectiveHoldingId);
  const isPending = mutationF3.isPending || mutation3A.isPending;

  // Reset on close so re-open starts fresh (entry prefill re-applied).
  useEffect(() => {
    if (open) return;
    setSubtype(entry?.defaultSubtype ?? "generic");
    setTitle("");
    setMode(entry?.holdingId ? "client" : "general");
    setHolding(entry?.holdingId ? { id: entry.holdingId, name: entry.holdingName ?? "" } : null);
    setPickerOpen(false);
    setContactId("");
    setDueDate("");
    setPriority("medium");
    setReminder("none");
    setAssigneeId("");
    setWatchers([]);
    setDescription("");
    setPendingAtt([]);
    setAttachmentsIds([]);
    setErrors({});
  }, [open, entry?.holdingId, entry?.holdingName, entry?.defaultSubtype]);

  if (!open) return null;

  const isStaged = !!staged;
  const sourceLabel = entry?.source ?? "вручну (manual create)";
  const contacts = contactsQ.data?.items ?? [];

  const canSubmit =
    title.trim().length > 0 &&
    dueDate.length > 0 &&
    (isStaged || mode === "general" || !!holding) &&
    !isPending;

  const submit = async () => {
    const e: typeof errors = {};
    if (!title.trim()) e.title = "Вкажіть назву";
    if (!isStaged && mode === "client" && !holding) e.client = "Оберіть клієнта або перемкніть на «Загальна»";
    if (!dueDate) e.dueDate = "Вкажіть дедлайн";
    if (Object.keys(e).length > 0) { setErrors(e); return; }
    setErrors({});

    // D-055 S3 staged: жодного POST — spec повертається у Actions Builder;
    // створення server-side на submit звіту (одна транзакція).
    if (staged) {
      staged.onStage({
        kind: subtype === "follow_up" ? "follow_up" : "task",
        title: title.trim(),
        dueDate,
        description: description.trim() || undefined,
      });
      onClose();
      return;
    }

    try {
      let res: TaskDetailResponse;
      if (mode === "client" && holding) {
        const payload: Client360TaskCreatePayload = {
          holdingId: holding.id,
          title: title.trim(),
          subtype,
          dueDate,
          priority,
          assigneeId: assigneeId || undefined,
          contactId: contactId || undefined,
          description: description.trim() || undefined,
          attachmentsIds: attachmentsIds.length > 0 ? attachmentsIds : undefined,
          watchersIds: watchers.length > 0 ? watchers.map((w) => w.id) : undefined,
        };
        res = await mutation3A.mutateAsync(payload);
      } else {
        const payload: TaskCreatePayload = {
          title: title.trim(),
          type: "general",
          subtype,
          dueDate,
          priority,
          cCReminderOffset: reminder,
          assigneeId: assigneeId || undefined,
          description: description.trim() || undefined,
          attachmentsIds: attachmentsIds.length > 0 ? attachmentsIds : undefined,
          watchersIds: watchers.length > 0 ? watchers.map((w) => w.id) : undefined,
        };
        res = await mutationF3.mutateAsync(payload);
      }
      onCreated?.(res);
      onClose();
    } catch (err) {
      setErrors({ submit: err instanceof Error ? err.message : "Не вдалося створити" });
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose} aria-hidden>
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        className="relative h-full w-full overflow-y-auto bg-background shadow-2xl outline-none sm:w-[440px]"
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-background px-5 py-4">
          <h3 id={headingId} className="text-base font-semibold">{isStaged ? "Дія зі звіту — задача" : "Нова задача"}</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Закрити форму"
            className="rounded-md p-1 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-5 px-5 py-5">
          {isStaged && (
            <p className="rounded-md border border-violet-foreground/20 bg-violet px-3 py-2 text-[11.5px] text-violet-foreground">
              Чернетка дії — створиться <b>після подання звіту</b> (одна транзакція). Клієнт і виконавець — із зустрічі.
            </p>
          )}
          {/* Subtype (R2: 3 manual + BI disabled) */}
          <div>
            <label className="mb-1.5 block text-xs font-medium">
              Тип <span className="text-destructive">*</span>
            </label>
            <div className={cn("grid gap-1.5", isStaged ? "grid-cols-2" : "grid-cols-3")} role="radiogroup" aria-label="Тип задачі">
              {(isStaged ? MANUAL_SUBTYPES.filter((s) => s.k !== "call") : MANUAL_SUBTYPES).map((s) => (
                <button
                  key={s.k}
                  type="button"
                  role="radio"
                  aria-checked={subtype === s.k}
                  onClick={() => setSubtype(s.k)}
                  className={cn(
                    "rounded-md border py-2 text-sm font-medium transition-colors",
                    subtype === s.k
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border text-muted-foreground hover:text-foreground",
                  )}
                >
                  {s.label}
                </button>
              ))}
            </div>
            <p className="mt-1 text-[11px] text-muted-foreground">
              BI-сигнал створюється системою. Контекст «звідки» — у «Джерело», не новий тип.
            </p>
          </div>

          {/* Title */}
          <div>
            <label className="mb-1.5 block text-xs font-medium">
              Назва <span className="text-destructive">*</span>
            </label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Напр. Передзвонити по дебіторці"
              aria-invalid={!!errors.title}
              className={cn(errors.title && "border-destructive")}
            />
            {errors.title && <p className="mt-1 text-xs text-destructive">{errors.title}</p>}
          </div>

          {/* Client / general (holding-grain) */}
          {!isStaged && (
          <div>
            <label className="mb-1.5 block text-xs font-medium">Клієнт / холдинг</label>
            {!clientLocked && (
              <div className="mb-2 grid grid-cols-2 gap-2" role="radiogroup" aria-label="Тип контексту">
                <button
                  type="button"
                  role="radio"
                  aria-checked={mode === "client"}
                  onClick={() => setMode("client")}
                  className={cn(
                    "flex items-center justify-center gap-1.5 rounded-md border py-2 text-sm font-medium transition-colors",
                    mode === "client"
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border text-muted-foreground hover:text-foreground",
                  )}
                >
                  <Building2 className="h-4 w-4" aria-hidden /> По клієнту
                </button>
                <button
                  type="button"
                  role="radio"
                  aria-checked={mode === "general"}
                  onClick={() => { setMode("general"); setHolding(null); setContactId(""); }}
                  className={cn(
                    "flex items-center justify-center gap-1.5 rounded-md border py-2 text-sm font-medium transition-colors",
                    mode === "general"
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border text-muted-foreground hover:text-foreground",
                  )}
                >
                  <Inbox className="h-4 w-4" aria-hidden /> Загальна
                </button>
              </div>
            )}
            {mode === "client" && (
              holding ? (
                <div className="flex items-center justify-between rounded-md border bg-muted/40 px-3 py-2 text-sm">
                  <span className="flex items-center gap-2 truncate">
                    <Building2 className="h-4 w-4 text-muted-foreground" aria-hidden />
                    {holding.name || "Поточний клієнт"}
                  </span>
                  {!clientLocked && (
                    <button
                      type="button"
                      onClick={() => setPickerOpen(true)}
                      className="text-xs text-muted-foreground hover:text-foreground"
                    >
                      Змінити
                    </button>
                  )}
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setPickerOpen(true)}
                  className={cn(
                    "flex w-full items-center gap-2 rounded-md border px-3 py-2 text-left text-sm text-muted-foreground hover:border-primary",
                    errors.client && "border-destructive",
                  )}
                >
                  <Building2 className="h-4 w-4" aria-hidden /> Обрати клієнта (холдинг)…
                </button>
              )
            )}
            {mode === "client" && (
              <p className="mt-1 text-[11px] text-muted-foreground">grain = holding</p>
            )}
            {errors.client && <p className="mt-1 text-xs text-destructive">{errors.client}</p>}
          </div>
          )}

          {/* Contact (only with holding context) */}
          {!isStaged && mode === "client" && holding && contacts.length > 0 && (
            <div>
              <label className="mb-1.5 block text-xs font-medium">
                Контакт <span className="font-normal text-muted-foreground">(опціонально)</span>
              </label>
              <select
                value={contactId}
                onChange={(e) => setContactId(e.target.value)}
                className="w-full rounded-md border bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <option value="">— не вказувати —</option>
                {contacts.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}{c.position ? ` · ${c.position}` : ""}</option>
                ))}
              </select>
            </div>
          )}

          {/* Due date */}
          <div>
            <label className="mb-1.5 block text-xs font-medium">
              Дедлайн <span className="text-destructive">*</span>
            </label>
            <div className="flex flex-wrap items-center gap-1.5">
              <div className={cn("flex items-center gap-2 rounded-md border px-2.5 py-1.5", errors.dueDate && "border-destructive")}>
                <Calendar className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                <input
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  className="bg-transparent text-sm outline-none"
                />
              </div>
              {([
                { label: "Сьогодні", d: 0 },
                { label: "Завтра", d: 1 },
                { label: "Тиждень", d: 7 },
              ] as const).map((q) => (
                <button
                  key={q.d}
                  type="button"
                  onClick={() => setDueDate(quickDatePlus(q.d))}
                  className={cn(
                    "rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors",
                    dueDate === quickDatePlus(q.d)
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border text-muted-foreground hover:text-foreground",
                  )}
                >
                  {q.label}
                </button>
              ))}
            </div>
            <p className="mt-1 text-[11px] text-muted-foreground">обов'язковий для open/planned задач</p>
            {errors.dueDate && <p className="mt-1 text-xs text-destructive">{errors.dueDate}</p>}
          </div>

          {/* Priority */}
          {!isStaged && (
          <div>
            <label className="mb-1.5 block text-xs font-medium">Пріоритет</label>
            <div className="grid grid-cols-3 gap-1.5">
              {(["high", "medium", "low"] as TaskPriorityApi[]).map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPriority(p)}
                  aria-pressed={priority === p}
                  className={cn(
                    "inline-flex items-center justify-center gap-1 rounded-md border py-1.5 text-xs font-medium transition-colors",
                    priority === p
                      ? p === "high"
                        ? "border-destructive bg-destructive/10 text-destructive"
                        : p === "medium"
                          ? "border-warning-foreground/20 bg-warning text-warning-foreground"
                          : "border-muted-foreground/40 bg-muted text-muted-foreground"
                      : "border-border text-muted-foreground hover:text-foreground",
                  )}
                >
                  <Flag className="h-3 w-3" aria-hidden /> {PRIORITY_LABELS[p]}
                </button>
              ))}
            </div>
            <p className="mt-1 text-[11px] text-muted-foreground">manual priority (≠ Home priorityRank)</p>
          </div>
          )}

          {/* Assignee — Q3 role-limited (manager → locked self) */}
          {!isStaged && (
          <div>
            <label className="mb-1.5 block text-xs font-medium">Виконавець</label>
            <ResponsibleSelect value={assigneeId} onChange={setAssigneeId} />
          </div>
          )}

          {/* Спостерігачі — D-142.W1 (read+stream, ≠виконавець) */}
          {!isStaged && (
          <div>
            <label className="mb-1.5 block text-xs font-medium">
              Спостерігачі <span className="font-normal text-muted-foreground">(опціонально)</span>
            </label>
            <WatcherPicker
              value={watchers}
              onChange={setWatchers}
              editable
              excludeIds={assigneeId ? [assigneeId] : []}
            />
            <p className="mt-1 text-[11px] text-muted-foreground">
              бачать задачу та її хід; не редагують і не отримують доступ до клієнта
            </p>
          </div>
          )}

          {/* Source — READ-ONLY prefill from entry-point */}
          {!isStaged && (
          <div>
            <label className="mb-1.5 block text-xs font-medium">Джерело</label>
            <div className="rounded-md border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">{sourceLabel}</div>
            <p className="mt-1 text-[11px] text-muted-foreground">prefill з entry-point, read-only — без fake-джерел</p>
          </div>
          )}

          {/* Reminder (F3 path; not persisted for client tasks) */}
          {!isStaged && mode === "general" && (
            <div>
              <label className="mb-1.5 block text-xs font-medium">Нагадування у CRM</label>
              <div className="flex flex-wrap gap-1.5">
                {REMINDER_OPTIONS.map((r) => (
                  <button
                    key={r.k}
                    type="button"
                    onClick={() => setReminder(r.k)}
                    className={cn(
                      "inline-flex items-center gap-1 rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors",
                      reminder === r.k
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-border text-muted-foreground hover:text-foreground",
                    )}
                  >
                    {r.k !== "none" && <Bell className="h-3 w-3" aria-hidden />} {r.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Description */}
          <div>
            <label className="mb-1.5 block text-xs font-medium">
              Опис <span className="text-muted-foreground">(опціонально)</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="w-full rounded-md border px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>

          {/* Attachments (W1) — create flow: parentId='' (no parent yet), saved=[].
              Staged mode (meeting-report entry) hides the picker: the real Task is
              created server-side on report submit, so there's no Attachment binding
              seam in the staged spec — keep staging unblocked. */}
          {!isStaged && (
            <AttachmentUpload
              parentType="Task"
              parentId=""
              saved={[]}
              pending={pendingAtt}
              onChange={(next) => {
                setAttachmentsIds(next.attachmentsIds);
                setPendingAtt(next.pending);
              }}
            />
          )}

          {errors.submit && (
            <p role="alert" className="flex items-center gap-1.5 text-xs text-destructive">
              <AlertCircle className="h-3.5 w-3.5" aria-hidden /> {errors.submit}
            </p>
          )}
        </div>

        <div className="sticky bottom-0 border-t bg-background px-5 py-4">
          <Button type="button" onClick={submit} disabled={!canSubmit} className="h-11 w-full text-sm font-semibold">
            {isPending ? (<><Loader2 className="h-4 w-4 animate-spin" /> Створення…</>) : isStaged ? "Додати до звіту" : "Створити задачу"}
          </Button>
        </div>
      </div>

      {/* holding-grain client picker (reusable no-context resolver) */}
      <ClientPickerDrawer
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        title="Задача — оберіть клієнта"
        onPick={(id, name) => {
          setHolding({ id, name });
          setContactId("");
          setPickerOpen(false);
        }}
      />
    </div>
  );
}
